import React from "react";
import { m as motion, MAnimatePresence as AnimatePresence } from "../../../shared/components/Motion";
import { formatTimeAgo } from "shared/utils";
import {
  childVariants,
  accordionContainer,
  accordionWrapper,
  accordionItem,
} from "shared/utils";
import { ButtonComponent } from "shared/components";
import "./VersionHistorySection.css";

/**
 * VersionHistorySection Component
 * Displays version history with restore and delete options
 */
const VersionHistorySection = ({
  isVersionHistoryExpanded,
  setIsVersionHistoryExpanded,
  allSections,
  loadingVersions,
  getVersionHistory,
  isCurrentPublishedVersion,
  onUndo,
  onDeleteVersion,
  refetchVersionHistory,
}) => {
  const [isRefreshing, setIsRefreshing] = React.useState(false);
  const [loadingState, setLoadingState] = React.useState({
    versionId: null,
    action: null,
  });

  const handleRefresh = async (e) => {
    e.stopPropagation(); // Prevent expanding/collapsing
    setIsRefreshing(true);
    try {
      // Force refresh from Firebase
      if (refetchVersionHistory) {
        await refetchVersionHistory(true);
      }
    } finally {
      setIsRefreshing(false);
    }
  };
  return (
    <motion.div
      className="themes__section"
      initial="hidden"
      animate="visible"
      variants={childVariants}
      style={{
        background:
          "color-mix(in srgb, var(--title-color) 15%, var(--body-color))",
        padding: "0",
        gap: "0",
      }}
    >
      {/* Header with toggle */}
      <motion.div
        initial="hidden"
        animate="visible"
        variants={childVariants}
        onClick={() => setIsVersionHistoryExpanded(!isVersionHistoryExpanded)}
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          width: "100%",
          padding: "0.75rem",
          background: "transparent",
          border: "none",
          cursor: "pointer",
          textAlign: "left",
          color: "var(--title-color)",
        }}
      >
        <motion.div
          initial="hidden"
          animate="visible"
          variants={childVariants}
          style={{
            display: "flex",
            alignItems: "center",
            gap: "0.5rem",
            fontSize: "0.85rem",
            fontWeight: "600",
          }}
        >
          <i
            className={`bx bx-chevron-${
              isVersionHistoryExpanded ? "down" : "right"
            }`}
          ></i>
          <span>Version History</span>
        </motion.div>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "0.5rem",
            background: "var(--title-color)",
            color: "var(--body-color)",
            padding: "0.25rem 0.5rem",
            borderRadius: "4px",
            fontSize: "0.75rem",
            fontWeight: "600",
          }}
        >
          <span>
            {allSections.reduce(
              (acc, section) => acc + getVersionHistory(section.id).length,
              0
            )}
          </span>
        </div>
      </motion.div>

      {/* Expanded content */}
      <AnimatePresence mode="wait">
        {isVersionHistoryExpanded && (
          <motion.div
            variants={accordionContainer}
            initial="hidden"
            animate="visible"
            exit="exit"
            style={{ overflow: "hidden" }}
          >
            {loadingVersions ? (
              <motion.div
                className="themes__section"
                variants={childVariants}
                initial="hidden"
                animate="visible"
                transition={{ duration: 0.3, ease: "easeInOut" }}
                style={{
                  padding: "1rem",
                  gap: "0.5rem",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "var(--text-color)",
                }}
              >
                <motion.div
                  animate={{ rotate: 360 }}
                  transition={{ duration: 1, repeat: Infinity }}
                  style={{ display: "flex" }}
                >
                  <i
                    className="bx bx-loader"
                    style={{ fontSize: "0.9rem" }}
                  ></i>
                </motion.div>
                <span style={{ fontSize: "0.75rem" }}>
                  Loading version history...
                </span>
              </motion.div>
            ) : (
              <motion.div
                variants={accordionWrapper}
                initial="hidden"
                animate="visible"
                exit="exit"
                style={{
                  gap: "0",
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                {allSections.filter(
                  (section) => getVersionHistory(section.id).length > 0
                ).length === 0 ? (
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: "0.5rem",
                      padding: "1rem",
                      color: "var(--text-color)",
                      fontSize: "0.75rem",
                      textAlign: "center",
                    }}
                  >
                    <i className="bx bx-check-circle"></i>
                    <span>No version history</span>
                  </div>
                ) : (
                  <div
                    style={{
                      width: "100%",
                      overflow: "hidden",
                    }}
                  >
                    {/* Scrollable versions list */}
                    <motion.div
                      style={{
                        position: "relative",
                        display: "flex",
                        flexDirection: "column",
                        gap: "0.25rem",
                        margin: "0.5rem",
                      }}
                      className="version-history-scroll"
                    >
                      <AnimatePresence mode="popLayout">
                        {(() => {
                          // Flatten all versions from all sections and sort by timestamp (newest first)
                          const allVersions = allSections
                            .flatMap((section) => {
                              const versionHistory = getVersionHistory(
                                section.id
                              );
                              return versionHistory.map((version) => ({
                                ...version,
                                sectionId: section.id,
                                sectionLabel: section.label,
                              }));
                            })
                            .sort((a, b) => {
                              const timeA =
                                a.timestamp instanceof Date
                                  ? a.timestamp.getTime()
                                  : new Date(a.timestamp).getTime();
                              const timeB =
                                b.timestamp instanceof Date
                                  ? b.timestamp.getTime()
                                  : new Date(b.timestamp).getTime();
                              return timeB - timeA; // Newest first
                            });

                          return allVersions.map((version) => (
                            <motion.div
                              className="glass__card"
                              variants={accordionItem}
                              initial="hidden"
                              animate="visible"
                              exit="exit"
                              key={version.id}
                              style={{
                                display: "flex",
                                flexDirection: "column",
                                gap: "0.3rem",
                                margin: "0 0.5rem",
                                padding: "0.4rem 0.5rem",
                                background: "var(--body-color)",
                                borderRadius: "3px",
                                flex: "0 0 auto",
                              }}
                            >
                              {/* First row: Section, version, badge */}
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "0.4rem",
                                  fontSize: "0.65rem",
                                  flex: 1,
                                  minWidth: 0,
                                }}
                              >
                                <span
                                  style={{
                                    fontWeight: "600",
                                    color: "var(--title-color)",
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap",
                                  }}
                                >
                                  {version.sectionLabel}
                                </span>
                                <span
                                  style={{
                                    fontWeight: "700",
                                    color: "var(--text-color)",
                                    whiteSpace: "nowrap",
                                  }}
                                >
                                  v0.{version.version_num}
                                </span>
                                {isCurrentPublishedVersion(
                                  version.sectionId,
                                  version.data
                                ) && (
                                  <span
                                    title="Current Published Version"
                                    style={{
                                      fontSize: "0.65rem",
                                      color: "var(--title-color)",
                                      fontWeight: "700",
                                      padding: "0.1rem 0.35rem",
                                      background:
                                        "color-mix(in srgb, var(--title-color) 20%, var(--body-color))",
                                      borderRadius: "2px",
                                      whiteSpace: "nowrap",
                                    }}
                                  >
                                    Latest
                                  </span>
                                )}
                              </div>
                              {/* Second row: Timestamp and buttons */}
                              <div
                                style={{
                                  display: "flex",
                                  justifyContent: "space-between",
                                  alignItems: "center",
                                  gap: "0.4rem",
                                }}
                              >
                                <span
                                  style={{
                                    color: "var(--text-color)",
                                    opacity: 0.6,
                                    whiteSpace: "nowrap",
                                    fontSize: "0.6rem",
                                    flex: 1,
                                  }}
                                >
                                  {formatTimeAgo(version.timestamp)}
                                </span>
                                <div
                                  style={{
                                    display: "flex",
                                    gap: "0.25rem",
                                    flexShrink: 0,
                                  }}
                                >
                                  {!isCurrentPublishedVersion(
                                    version.sectionId,
                                    version.data
                                  ) && (
                                    <button
                                      onClick={async () => {
                                        setLoadingState({
                                          versionId: version.id,
                                          action: "restore",
                                        });
                                        try {
                                          const success = await onUndo(
                                            allSections.find(
                                              (s) => s.id === version.sectionId
                                            ),
                                            version.id
                                          );
                                          // Only refetch if operation succeeded (not cancelled)
                                          if (
                                            success &&
                                            refetchVersionHistory
                                          ) {
                                            await new Promise((resolve) =>
                                              setTimeout(resolve, 300)
                                            );
                                            await refetchVersionHistory(true);
                                          }
                                        } finally {
                                          setLoadingState({
                                            versionId: null,
                                            action: null,
                                          });
                                        }
                                      }}
                                      title="Restore this version"
                                      disabled={
                                        loadingState.versionId === version.id &&
                                        loadingState.action === "restore"
                                      }
                                      style={{
                                        display: "flex",
                                        alignItems: "center",
                                        padding: "0.25rem 0.35rem",
                                        background:
                                          "linear-gradient(135deg, rgb(102, 126, 234) 0%, rgb(118, 75, 162) 100%)",
                                        color: "white",
                                        border: "none",
                                        borderRadius: "2px",
                                        cursor:
                                          loadingState.versionId ===
                                            version.id &&
                                          loadingState.action === "restore"
                                            ? "not-allowed"
                                            : "pointer",
                                        transition: "all 0.2s ease",
                                        opacity:
                                          loadingState.versionId ===
                                            version.id &&
                                          loadingState.action === "restore"
                                            ? 0.7
                                            : 1,
                                      }}
                                    >
                                      {loadingState.versionId === version.id &&
                                      loadingState.action === "restore" ? (
                                        <motion.div
                                          animate={{ rotate: 360 }}
                                          transition={{
                                            duration: 1,
                                            repeat: Infinity,
                                          }}
                                          style={{ display: "flex" }}
                                        >
                                          <i
                                            className="bx bx-loader"
                                            style={{ fontSize: "0.75rem" }}
                                          ></i>
                                        </motion.div>
                                      ) : (
                                        <i
                                          className="bx bx-undo"
                                          style={{ fontSize: "0.75rem" }}
                                        ></i>
                                      )}
                                    </button>
                                  )}
                                  <button
                                    onClick={async () => {
                                      setLoadingState({
                                        versionId: version.id,
                                        action: "delete",
                                      });
                                      try {
                                        const success = await onDeleteVersion(
                                          version.sectionId,
                                          version.id
                                        );
                                        // Only refetch if operation succeeded (not cancelled)
                                        if (success) {
                                          // Small delay to ensure Firestore has written the deletion
                                          await new Promise((resolve) =>
                                            setTimeout(resolve, 300)
                                          );
                                          // Refetch version history after delete with force flag
                                          if (refetchVersionHistory) {
                                            await refetchVersionHistory(true);
                                          }
                                        }
                                      } finally {
                                        setLoadingState({
                                          versionId: null,
                                          action: null,
                                        });
                                      }
                                    }}
                                    title="Delete this version"
                                    disabled={
                                      loadingState.versionId === version.id &&
                                      loadingState.action === "delete"
                                    }
                                    style={{
                                      display: "flex",
                                      alignItems: "center",
                                      padding: "0.25rem 0.35rem",
                                      background: "#ef4444",
                                      color: "white",
                                      border: "none",
                                      borderRadius: "2px",
                                      cursor:
                                        loadingState.versionId === version.id &&
                                        loadingState.action === "delete"
                                          ? "not-allowed"
                                          : "pointer",
                                      transition: "all 0.2s ease",
                                      opacity:
                                        loadingState.versionId === version.id &&
                                        loadingState.action === "delete"
                                          ? 0.7
                                          : 1,
                                    }}
                                    onMouseEnter={(e) => {
                                      if (
                                        !(
                                          loadingState.versionId ===
                                            version.id &&
                                          loadingState.action === "delete"
                                        )
                                      ) {
                                        e.currentTarget.style.background =
                                          "#dc2626";
                                        e.currentTarget.style.transform =
                                          "translateY(-1px)";
                                      }
                                    }}
                                    onMouseLeave={(e) => {
                                      if (
                                        !(
                                          loadingState.versionId ===
                                            version.id &&
                                          loadingState.action === "delete"
                                        )
                                      ) {
                                        e.currentTarget.style.background =
                                          "#ef4444";
                                        e.currentTarget.style.transform =
                                          "translateY(0)";
                                      }
                                    }}
                                  >
                                    {loadingState.versionId === version.id &&
                                    loadingState.action === "delete" ? (
                                      <motion.div
                                        animate={{ rotate: 360 }}
                                        transition={{
                                          duration: 1,
                                          repeat: Infinity,
                                        }}
                                        style={{ display: "flex" }}
                                      >
                                        <i
                                          className="bx bx-loader"
                                          style={{ fontSize: "0.75rem" }}
                                        ></i>
                                      </motion.div>
                                    ) : (
                                      <i
                                        className="bx bx-trash"
                                        style={{ fontSize: "0.75rem" }}
                                      ></i>
                                    )}
                                  </button>
                                </div>
                              </div>
                            </motion.div>
                          ));
                        })()}
                      </AnimatePresence>
                    </motion.div>

                    {/* Refresh Button */}
                    <motion.div
                      variants={childVariants}
                      initial="hidden"
                      animate="visible"
                      transition={{ duration: 0.3, ease: "easeInOut" }}
                      style={{
                        display: "flex",
                        justifyContent: "center",
                        padding: "0.5rem",
                      }}
                    >
                      <ButtonComponent
                        buttonTitle="Refresh Version History"
                        buttonText="Refresh Version History"
                        onClick={handleRefresh}
                        disabled={isRefreshing}
                        className={`version-refresh-btn ${
                          isRefreshing ? "loading" : ""
                        }`}
                      >
                        {isRefreshing ? (
                          <>
                            <motion.div
                              animate={{ rotate: 360 }}
                              transition={{ duration: 1, repeat: Infinity }}
                              style={{ display: "flex" }}
                            >
                              <i
                                className="bx bx-loader"
                                style={{ fontSize: "0.8rem" }}
                              ></i>
                            </motion.div>
                            <span>Refreshing...</span>
                          </>
                        ) : (
                          <>
                            <i
                              className="bx bx-refresh"
                              style={{ fontSize: "0.8rem" }}
                            ></i>
                            <span>Refresh Version History</span>
                          </>
                        )}
                      </ButtonComponent>
                    </motion.div>
                  </div>
                )}
              </motion.div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export default VersionHistorySection;
